import { Button, Popover, Tag, Typography } from "antd";
import { CaretDownOutlined, CaretRightOutlined, FileTextOutlined } from "@ant-design/icons";
import { FC, useState } from "react";
import { IDryRunStepNode } from "../utils/dryRunCalculateEquation";
import { minifyEquation } from "../../utils/equationParser";
import ViewFormattedEquationButton from "./ViewFormattedEquationButton";

const { Text } = Typography;

const ResultBadge: FC<{ result: number }> = ({ result }) => (
  <Tag className={result ? "dryRunResultTrue" : "dryRunResultFalse"}>{result}</Tag>
);

// Real connecting lines, drawn with CSS borders on a nested <ul>/<li> structure (the standard
// "org chart" tree technique) rather than Unicode "├──"/"│" characters — text glyphs don't
// reliably line up across rows of different heights (a Tag badge vs. plain text), so the lines
// looked broken. Each <li> draws its own vertical drop (::before) and horizontal branch
// (::after) via CSS; nested <ul>s are scoped to their parent <li>, so lines only span that
// parent's own children and naturally stop at the last one.
const DryRunTreeNode: FC<{ node: IDryRunStepNode }> = ({ node }) => {
  const [isImportExpanded, setIsImportExpanded] = useState(false);
  const children = node.children ?? [];
  const hasImportedTree = node.kind === "import" && !!node.importedTree;

  return (
    <li>
      <div className="dryRunTreeRow">
        {node.kind !== "literal" && <ResultBadge result={node.result} />}
        <Text code className={node.kind === "literal" ? "dryRunStepLabel dryRunStepLiteral" : "dryRunStepLabel"}>
          {node.label}
        </Text>
        {node.answerValue !== undefined && (
          <Popover
            trigger="click"
            placement="right"
            overlayClassName="dryRunAnswerPopover"
            content={<div className="dryRunAnswerPopoverContent">{node.answerValue}</div>}
          >
            <Button
              type="text"
              size="small"
              icon={<FileTextOutlined />}
              className="dryRunViewFormattedBtn"
              onClick={(e) => e.stopPropagation()}
            />
          </Popover>
        )}
        {node.detail && (
          <Text type="danger" className="dryRunStepDetail">
            {node.detail}
          </Text>
        )}
        {hasImportedTree && (
          <Button
            type="link"
            size="small"
            className="dryRunImportToggle"
            onClick={() => setIsImportExpanded((prev) => !prev)}
          >
            {isImportExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}{" "}
            {isImportExpanded ? "Collapse" : "Expand"}
          </Button>
        )}
      </div>
      {children.length > 0 && (
        <ul>
          {children.map((child, index) => (
            <DryRunTreeNode key={index} node={child} />
          ))}
        </ul>
      )}
      {/* An IMPORT'd rank's equation can be just as big as the current one — so its
          breakdown gets the same treatment the modal gives the equation actually being dry-run,
          nested as its own independent tree root (not stitched onto the parent's connector
          lines, since it's a different rank's equation entirely). */}
      {hasImportedTree && isImportExpanded && (
        <div className="dryRunImportedBlock">
          <div className="dryRunImportedBlockHeader">
            <span className="dryRunEquationHeaderLabel">
              <Text strong>Imported equation</Text>
              <ViewFormattedEquationButton equationText={node.importedEquationText ?? ""} />
            </span>
            <ResultBadge result={node.importedTree!.result} />
          </div>
          <div className="dryRunImportedEquationBox">
            <pre className="dryRunEquationText">{minifyEquation(node.importedEquationText ?? "")}</pre>
          </div>
          <ul className="dryRunTree dryRunTreeRoot">
            <DryRunTreeNode node={node.importedTree!} />
          </ul>
        </div>
      )}
    </li>
  );
};

// Only the OUTERMOST tree gets "dryRunTreeScrollArea" — nested imported-equation trees (above,
// inside .dryRunImportedBlock) reuse "dryRunTree dryRunTreeRoot" without it, so scrolling/height
// clamping only ever applies once per modal, not once per expanded IMPORT.
const DryRunStepNode: FC<{ node: IDryRunStepNode }> = ({ node }) => (
  <ul className="dryRunTree dryRunTreeRoot dryRunTreeScrollArea">
    <DryRunTreeNode node={node} />
  </ul>
);

export default DryRunStepNode;
